// Renders the verification status of an airframe.
import { CheckCircle2, AlertTriangle, ShieldAlert, Clock } from "lucide-react";

const STATE_META = {
    verified: {
        label: "Verified",
        cls: "border-ux-success/40 text-ux-success bg-ux-success/10",
        Icon: CheckCircle2,
    },
    failed: {
        label: "Failed",
        cls: "border-ux-danger/40 text-ux-danger bg-ux-danger/10",
        Icon: ShieldAlert,
    },
    unverified: {
        label: "Unverified",
        cls: "border-ux-warning/40 text-ux-warning bg-ux-warning/10",
        Icon: AlertTriangle,
    },
    running: {
        label: "Running",
        cls: "border-ux-accent/40 text-ux-accent bg-ux-accent/10",
        Icon: Clock,
    },
};

function StateBadge({ state }) {
    const meta = STATE_META[state] || STATE_META.unverified;
    const Icon = meta.Icon;
    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.cls}`}
            data-testid={`verify-state-${state}`}
        >
            <Icon className="w-3 h-3" /> {meta.label}
        </span>
    );
}

export default function VerifyReport({ verification }) {
    if (!verification) {
        return (
            <p className="text-[12px] text-ux-text-3" data-testid="verify-report-empty">
                No verification data yet.
            </p>
        );
    }
    const issues = verification.issues || [];
    const blocking = issues.filter((i) => i.severity === "blocking");
    const warnings = issues.filter((i) => i.severity === "warning");
    return (
        <div className="space-y-3" data-testid="verify-report">
            <div className="flex items-center gap-2 flex-wrap">
                <StateBadge state={verification.state} />
                {verification.backend_used && (
                    <span className="text-[11px] font-mono text-ux-text-3">
                        backend: {verification.backend_used}
                    </span>
                )}
                {verification.last_run_duration_s != null && (
                    <span className="text-[11px] font-mono text-ux-text-3">
                        {(verification.last_run_duration_s).toFixed(2)} s
                    </span>
                )}
            </div>
            {verification.override_by && (
                <p
                    className="text-[11px] text-ux-warning font-mono"
                    data-testid="verify-override-note"
                >
                    Verification overridden by {verification.override_by}
                </p>
            )}
            {blocking.length > 0 && (
                <section data-testid="verify-blocking">
                    <h4 className="text-[11px] uppercase tracking-wider text-ux-danger font-mono mb-1">
                        Blocking ({blocking.length})
                    </h4>
                    <ul className="space-y-1">
                        {blocking.map((i, idx) => (
                            <li
                                key={`b-${idx}`}
                                className="text-[12px] text-ux-text-1 border-l-2 border-ux-danger pl-2"
                            >
                                <span className="font-mono text-[10px] text-ux-text-3 uppercase mr-2">
                                    {i.code}
                                </span>
                                {i.message}
                            </li>
                        ))}
                    </ul>
                </section>
            )}
            {warnings.length > 0 && (
                <section data-testid="verify-warnings">
                    <h4 className="text-[11px] uppercase tracking-wider text-ux-warning font-mono mb-1">
                        Warnings ({warnings.length})
                    </h4>
                    <ul className="space-y-1">
                        {warnings.map((i, idx) => (
                            <li
                                key={`w-${idx}`}
                                className="text-[12px] text-ux-text-2 border-l-2 border-ux-warning pl-2"
                            >
                                {i.message}
                            </li>
                        ))}
                    </ul>
                </section>
            )}
            {blocking.length === 0 && warnings.length === 0 && verification.state === "verified" && (
                <p
                    className="text-[12px] text-ux-success"
                    data-testid="verify-clean"
                >
                    Flight passed without issues.
                </p>
            )}
        </div>
    );
}
